import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { getUserDashboardState, upsertUserDashboardState } from '../services/userDashboardState';

// Fail-secure default: an unset/unreadable localStorage key means HIDDEN.
// This is a security-relevant convenience toggle (workshop/demo UI cleanup),
// so ambiguity must always resolve to hidden, never visible.
const readLocalWorkshopMode = () => {
  const stored = localStorage.getItem('workshopMode');
  return stored === null ? true : stored === 'true';
};

// Fallback for any consumer accidentally rendered outside the Provider —
// also fail-secure (hidden), never used in practice since WorkshopModeProvider
// wraps the whole app in index.jsx.
const WorkshopModeContext = createContext({ workshopMode: true, toggleWorkshopMode: () => {} });

/**
 * Single source of truth for Workshop Mode. Mounted in index.jsx (above
 * BrowserRouter, inside AuthProvider) so every route — Login, LandingPage,
 * ETLProjectPage, and the main App — shares this one instance instead of
 * each maintaining its own localStorage copy (that drift is what previously
 * produced two different, inconsistent defaults).
 *
 * Cross-device sync: the account's `workshop_mode` column in
 * `user_dashboard_state` is authoritative in BOTH directions once it is
 * definitively known (hide or reveal). Any uncertainty — not logged in,
 * fetch error/offline, no row yet — leaves the local fail-secure default
 * untouched. Buttons are never optimistically shown while state is
 * unresolved: the first paint is always the local hidden-by-default value.
 *
 * Shared-browser guard: on ANY identity change (login, logout, or switching
 * to a different account on the same browser) we immediately force the
 * fail-secure hidden default — synchronously, before attempting to fetch
 * the new identity's own value. Without this, a previously-synced account's
 * "visible" value would sit in localStorage and leak into the next
 * session on a shared/kiosk browser (a different account, or a logged-out
 * viewer) for the window before that session's own fetch resolves.
 */
export const WorkshopModeProvider = ({ children }) => {
  const { user } = useAuth();
  const [workshopMode, setWorkshopMode] = useState(readLocalWorkshopMode);
  const prevUserIdRef = useRef(user?.id ?? null);

  useEffect(() => {
    const currentUserId = user?.id ?? null;
    const identityChanged = prevUserIdRef.current !== currentUserId;
    prevUserIdRef.current = currentUserId;

    if (identityChanged) {
      // Never let one identity's synced value leak into the next session on
      // this browser — reset to hidden before anything else runs.
      localStorage.setItem('workshopMode', 'true');
      setWorkshopMode(true);
    }

    if (!currentUserId) return;
    let cancelled = false;

    getUserDashboardState()
      .then((state) => {
        if (cancelled) return;
        if (typeof state?.workshop_mode !== 'boolean') return; // no row yet / fail-secure: keep hidden default
        const serverValue = state.workshop_mode;
        localStorage.setItem('workshopMode', String(serverValue));
        setWorkshopMode(serverValue);
      })
      .catch(() => {}); // fail-secure: leave the existing hidden default untouched

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const toggleWorkshopMode = useCallback(() => {
    setWorkshopMode((prev) => {
      const next = !prev;
      localStorage.setItem('workshopMode', String(next));
      if (user?.id) {
        upsertUserDashboardState({ workshop_mode: next }).catch(() => {});
      }
      return next;
    });
  }, [user?.id]);

  return (
    <WorkshopModeContext.Provider value={{ workshopMode, toggleWorkshopMode }}>
      {children}
    </WorkshopModeContext.Provider>
  );
};

export const useWorkshopMode = () => useContext(WorkshopModeContext);
